import { supabase } from './supabase';

export interface TypographyPreset {
  id: string;
  workspace_id: string;
  name: string;
  description: string;
  naming_convention: string;
  min_font_size: number;
  max_font_size: number;
  min_scale_ratio: number;
  max_scale_ratio: number;
  base_scale_index: string;
  scale_mode: 'automatic' | 'manual';
  heading_font_family: string;
  body_font_family: string;
  heading_font_weight?: string;
  heading_line_height?: number;
  heading_letter_spacing?: number;
  body_font_weight?: string;
  body_line_height?: number;
  body_letter_spacing?: number;
  created_at: string;
  updated_at: string;
  created_by: string;
}

export interface CreateTypographyPresetData {
  workspace_id: string;
  name: string;
  description?: string;
  naming_convention?: string;
  min_font_size?: number;
  max_font_size?: number;
  min_scale_ratio?: number;
  max_scale_ratio?: number;
  base_scale_index?: string;
  scale_mode?: 'automatic' | 'manual';
  heading_font_family?: string;
  body_font_family?: string;
  heading_font_weight?: string;
  heading_line_height?: number;
  heading_letter_spacing?: number;
  body_font_weight?: string;
  body_line_height?: number;
  body_letter_spacing?: number;
}

export interface UpdateTypographyPresetData {
  name?: string;
  description?: string;
  naming_convention?: string;
  min_font_size?: number;
  max_font_size?: number;
  min_scale_ratio?: number;
  max_scale_ratio?: number;
  base_scale_index?: string;
  scale_mode?: 'automatic' | 'manual';
  heading_font_family?: string;
  body_font_family?: string;
  heading_font_weight?: string;
  heading_line_height?: number;
  heading_letter_spacing?: number;
  body_font_weight?: string;
  body_line_height?: number;
  body_letter_spacing?: number;
}

export const typographyPresetService = {
  async getPresetsByWorkspace(workspaceId: string): Promise<TypographyPreset[]> {
    const { data, error } = await supabase
      .from('typography_presets')
      .select('*')
      .eq('workspace_id', workspaceId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async getPresetById(id: string): Promise<TypographyPreset | null> {
    const { data, error } = await supabase
      .from('typography_presets')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  async createPreset(presetData: CreateTypographyPresetData): Promise<TypographyPreset> {
    const { data: userData } = await supabase.auth.getUser();
    if (!userData.user) throw new Error('User not authenticated');

    console.log('Creating preset with data:', presetData);
    console.log('User ID:', userData.user.id);

    const { data, error } = await supabase
      .from('typography_presets')
      .insert({
        ...presetData,
        created_by: userData.user.id,
      })
      .select()
      .single();

    if (error) {
      console.error('Supabase error:', error);
      throw new Error(`Failed to create preset: ${error.message}`);
    }

    console.log('Created preset successfully:', data);
    return data;
  },

  async updatePreset(id: string, presetData: UpdateTypographyPresetData): Promise<TypographyPreset> {
    const { data, error } = await supabase
      .from('typography_presets')
      .update(presetData)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async deletePreset(id: string): Promise<void> {
    const { error } = await supabase
      .from('typography_presets')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  async duplicatePreset(id: string, newName: string): Promise<TypographyPreset> {
    const preset = await this.getPresetById(id);
    if (!preset) throw new Error('Preset not found');

    const { data: userData } = await supabase.auth.getUser();
    if (!userData.user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('typography_presets')
      .insert({
        workspace_id: preset.workspace_id,
        name: newName,
        description: preset.description,
        naming_convention: preset.naming_convention,
        min_font_size: preset.min_font_size,
        max_font_size: preset.max_font_size,
        min_scale_ratio: preset.min_scale_ratio,
        max_scale_ratio: preset.max_scale_ratio,
        base_scale_index: preset.base_scale_index,
        scale_mode: preset.scale_mode,
        heading_font_family: preset.heading_font_family,
        body_font_family: preset.body_font_family,
        heading_font_weight: preset.heading_font_weight,
        heading_line_height: preset.heading_line_height,
        heading_letter_spacing: preset.heading_letter_spacing,
        body_font_weight: preset.body_font_weight,
        body_line_height: preset.body_line_height,
        body_letter_spacing: preset.body_letter_spacing,
        created_by: userData.user.id,
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },
};
